import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import * as R from 'ramda';
import {Evolver} from 'ramda';
import {Err, err, Ok, ok} from 'neverthrow';
import {
  BadResponseException,
  InternalServerException,
  NotFoundException,
  RateLimitException,
  TimeoutException,
  UnauthenticatedException,
  ValidationException,
} from './exceptions';
import {
  ApiClient,
  ApiError,
  ApiErrorKind,
  ApiResponse,
  ApiResult,
} from './types';

export const HEADER_KEY_ASSUME_PRACTICE_ID = 'X-PG-PID';

/**
 * Adds error interceptors that throw domain-specific exceptions
 */
const handleHTTPErrorsUnsafely = (client: AxiosInstance): AxiosInstance => {
  client.interceptors.response.use(undefined, ({code, response}) => {
    if (code === 'ECONNABORTED') {
      throw new TimeoutException();
    }

    switch (response.status) {
      case 401:
      case 403:
        throw new UnauthenticatedException(response);
      case 404:
        throw new NotFoundException(response);
      case 408:
        throw new TimeoutException();
      case 422:
        throw new ValidationException(response.data, response);
      case 429:
        throw new RateLimitException(response);
      case 500:
        throw new InternalServerException(response);
      default:
        throw new BadResponseException(response);
    }
  });

  return client;
};

const getResponseError = (response: AxiosResponse | AxiosError): ApiError => {
  if ((response as AxiosError).code === 'ECONNABORTED') {
    return {kind: ApiErrorKind.TimeoutError};
  }
  const error = {response: response as AxiosResponse};

  switch ((response as AxiosResponse).status) {
    case 401:
    case 403:
      return {...error, kind: ApiErrorKind.UnauthenticatedError};
    case 404:
      return {...error, kind: ApiErrorKind.NotFoundError};
    case 408:
      return {...error, kind: ApiErrorKind.TimeoutError};
    case 422:
      return {
        ...error,
        kind: ApiErrorKind.ValidationError,
        errors: (response as AxiosResponse).data.errors,
      };
    case 429:
      return {...error, kind: ApiErrorKind.RateLimitError};
    case 500:
      return {...error, kind: ApiErrorKind.InternalServerError};
    default:
      return {...error, kind: ApiErrorKind.BadResponseError};
  }
};

const makeErrResult = <D>(respError: ApiError): ApiResult<D> => {
  const errResult = err(respError) as ApiResult<D>;

  errResult.mapData = <X>(): Err<X, ApiError> => err(respError);
  return errResult;
};

const makeOkResult = <D>(response: AxiosResponse<D>): ApiResult<D> => {
  const okResult = ok(response) as ApiResult<D>;
  okResult.mapData = <X>(mapper: (data: D) => X): Ok<X, ApiError> =>
    ok(mapper(response.data));
  return okResult;
};

const resultifyResponse = <D>(
  responseOrErrorOrException: AxiosResponse<D> | AxiosError | Error,
): ApiResult<D> => {
  const exception = responseOrErrorOrException as Error;
  const error = responseOrErrorOrException as AxiosError;
  const response = responseOrErrorOrException as AxiosResponse;

  if (exception instanceof Error) {
    return makeErrResult<D>({kind: ApiErrorKind.TimeoutError});
  }

  if (response.status > 399 || error.code) {
    return makeErrResult<D>(getResponseError(response));
  }

  return makeOkResult(response);
};

const addDefaultResponseTransformer = (
  cfg?: AxiosRequestConfig,
): AxiosRequestConfig | undefined => {
  if (cfg)
    return R.evolve<Evolver, AxiosRequestConfig>(
      {
        transformResponse: R.flip(R.append)([JSON.parse]),
      },
      cfg,
    );
};

const resultifyClientInstance = (
  safeInstance: AxiosInstance,
): Omit<ApiClient, '_unsafe'> => {
  function get<D>(url: string, config?: AxiosRequestConfig): ApiResponse<D> {
    return safeInstance
      .get(url, addDefaultResponseTransformer(config))
      .then(resultifyResponse)
      .catch(err => resultifyResponse<D>(err));
  }

  function _delete<D>(
    url: string,
    config?: AxiosRequestConfig,
  ): ApiResponse<D> {
    return safeInstance
      .delete(url, addDefaultResponseTransformer(config))
      .then(resultifyResponse)
      .catch(err => resultifyResponse<D>(err));
  }

  function head<D>(url: string, config?: AxiosRequestConfig): ApiResponse<D> {
    return safeInstance
      .head(url, addDefaultResponseTransformer(config))
      .then(resultifyResponse)
      .catch(err => resultifyResponse<D>(err));
  }

  function options<D>(
    url: string,
    config?: AxiosRequestConfig,
  ): ApiResponse<D> {
    return safeInstance
      .options(url, addDefaultResponseTransformer(config))
      .then(resultifyResponse)
      .catch(err => resultifyResponse<D>(err));
  }

  function post<In, Out>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig,
  ): ApiResponse<Out> {
    return safeInstance
      .post(url, data, addDefaultResponseTransformer(config))
      .then(resultifyResponse)
      .catch(err => resultifyResponse<Out>(err));
  }

  function put<In, Out>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig,
  ): ApiResponse<Out> {
    return safeInstance
      .put(url, data, addDefaultResponseTransformer(config))
      .then(resultifyResponse)
      .catch(err => resultifyResponse<Out>(err));
  }

  function patch<In, Out>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig,
  ): ApiResponse<Out> {
    return safeInstance
      .patch(url, data, addDefaultResponseTransformer(config))
      .then(resultifyResponse)
      .catch(err => resultifyResponse<Out>(err));
  }

  return {get, delete: _delete, head, options, post, put, patch};
};

type NoAuthApiClientFactory = (
  config: AxiosRequestConfig & {
    baseURL?: string;
  },
) => ApiClient;
/**
 * Generates an ApiClient instance for making requests that don't require authentication
 */
export const makeNoAuthApiClient: NoAuthApiClientFactory = config => {
  const unsafeInstance = (
    R.pipe(axios.create, handleHTTPErrorsUnsafely) as (
      c: typeof config,
    ) => AxiosInstance
  )({
    ...config,
  });

  const safeInstance = axios.create({
    ...config,
    validateStatus: R.T,
  });

  return {
    _unsafe: unsafeInstance,
    ...resultifyClientInstance(safeInstance),
  };
};

export const makeInterserviceApiClient = (
  clientConfig: Omit<AxiosRequestConfig, 'validateStatus'> & {
    baseURL: string;
    interserviceToken: string;
    practiceId?: number;
  },
) => {
  const {interserviceToken, practiceId, ...config} = clientConfig;

  const headers: Record<string, unknown> = {
    Authorization: `Basic ${interserviceToken}`,
  };

  if (practiceId) headers[HEADER_KEY_ASSUME_PRACTICE_ID] = practiceId;

  const axiosConfig = {
    ...config,
    headers: {
      ...clientConfig.headers,
      ...headers,
    },
  };

  const unsafeInstance = (
    R.pipe(axios.create, handleHTTPErrorsUnsafely) as (
      c: typeof axiosConfig,
    ) => AxiosInstance
  )({
    ...axiosConfig,
  });

  const safeInstance = axios.create({
    ...axiosConfig,
    validateStatus: R.T,
  });

  return {
    _unsafe: unsafeInstance,
    ...resultifyClientInstance(safeInstance),
  };
};

type AuthApiClientFactory = (
  clientConfig: Omit<AxiosRequestConfig, 'validateStatus'> & {
    baseURL?: string;
    authToken: string;
  },
) => ApiClient;
/**
 * Generates an ApiClient instance for making requests that require authentication
 */
export const makeAuthApiClient: AuthApiClientFactory = clientConfig => {
  const _auth = clientConfig.authToken;

  const makeAxiosConfig = (): AxiosRequestConfig => ({
    baseURL: clientConfig.baseURL,
    headers: {
      Authorization: `Bearer ${_auth}`,
    },
  });

  /**
   * If certain environment conditions are met,
   * configures an interceptor that logs request/responses in our RequestSpec format
   */
  const bindRequestSpecLogger = (instance: AxiosInstance): AxiosInstance => {
    const logForConfig = (
      config: AxiosRequestConfig,
      response?: any,
      status?: number,
    ) => {
      if (config.method) {
        const reqData = config.data;
        const requestSpec: Record<string, any> = {
          [config.method]: reqData
            ? [`/${config.url}`, JSON.parse(reqData)]
            : [`/${config.url}`],
        };

        if (config.params) requestSpec.query = config.params;

        requestSpec.reply = [status, response || 'no response'];

        // eslint-disable-next-line no-console
        console.log('requestSpec\n', JSON.stringify(requestSpec, null, 2));
      }
    };

    if (
      typeof window !== 'undefined' &&
      typeof process !== 'undefined' &&
      (process.env.LOG_REQUEST_SPEC ||
        localStorage?.getItem?.('LOG_REQUEST_SPEC'))
    ) {
      instance.interceptors.response.use(
        response => {
          logForConfig(response.config, response.data, response.status);

          return response;
        },
        error => {
          // Unfortunately it seems that there's no way to get the response body here
          logForConfig(error.config, error.message);
          throw error;
        },
      );
    }

    return instance;
  };

  const createSafeInstance = (): Omit<ApiClient, '_unsafe'> =>
    (
      R.pipe(axios.create, bindRequestSpecLogger, resultifyClientInstance) as (
        config: AxiosRequestConfig,
      ) => ReturnType<typeof resultifyClientInstance>
    )({
      ...makeAxiosConfig(),
      validateStatus: R.T,
    });

  async function get<D>(
    url: string,
    config: AxiosRequestConfig,
  ): ApiResponse<D> {
    const instance = createSafeInstance();
    return instance.get(url, config);
  }
  async function _delete<D>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResult<D>> {
    const instance = createSafeInstance();
    return instance.delete(url, config);
  }
  async function head<D>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResult<D>> {
    const instance = createSafeInstance();
    return instance.head(url, config);
  }
  async function options<D>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResult<D>> {
    const instance = createSafeInstance();
    return instance.options(url, config);
  }
  async function patch<In, Out>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig,
  ): Promise<ApiResult<Out>> {
    const instance = createSafeInstance();
    return instance.patch(url, data, config);
  }
  async function post<In, Out>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig,
  ): Promise<ApiResult<Out>> {
    const instance = createSafeInstance();
    return instance.post(url, data, config);
  }
  async function put<In, Out>(
    url: string,
    data?: In,
    config?: AxiosRequestConfig,
  ): Promise<ApiResult<Out>> {
    const instance = createSafeInstance();
    return instance.put(url, data, config);
  }

  return {
    get _unsafe(): AxiosInstance {
      return R.pipe(
        makeAxiosConfig,
        axios.create,
        bindRequestSpecLogger,
        handleHTTPErrorsUnsafely,
      )();
    },
    get,
    delete: _delete,
    head,
    options,
    patch,
    post,
    put,
  };
};

/**
 * Utility for creating ApiResults from unknown data (for testing)
 */
export const makeApiResult = <D>(
  response: unknown,
  isErr = false,
): ApiResult<D> =>
  isErr
    ? makeErrResult(response as ApiError)
    : makeOkResult(response as AxiosResponse<D>);

/**
 * Utility for creating ApiError instances from a partial ApiError object
 */
export const makeApiError = (err: {
  kind: ApiErrorKind;
  response?: AxiosResponse;
  errors?: Record<string, unknown>;
}): ApiError => err as ApiError;

/**
 * The methods on a NilApiClient instance don't do anything but return an error, but it's useful when you want to
 * instantiate something that requires an ApiClient instance before you can make a real one,
 * and you don't want to handle null checks everywhere.
 */
export const makeNilApiClient = (): ApiClient => {
  const error = makeApiError({kind: ApiErrorKind.InternalServerError});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handler = R.always(Promise.resolve(makeErrResult<any>(error)));

  return {
    _unsafe: axios.create(),
    get: handler,
    delete: handler,
    head: handler,
    options: handler,
    post: handler,
    put: handler,
    patch: handler,
  };
};
